import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { FunctionComponent, SVGProps } from 'react';
import Pagination from '../../components/Pagination';
import {
  NewEngagementIcon,
  EngSearchIcon,
  EditSquareIcon,
  EngDeleteIcon,
  StatTrendIcon,
  StatClosedIcon,
  StatUpcomingIcon,
  StatCompletedIcon,
  TileOutreachIcon,
  TileTownhallIcon,
  TileManifestoIcon,
  TileGrassrootsIcon,
} from '../../assets/icons';
import david from '../../assets/avatars/david-chen.png';
import sarah from '../../assets/avatars/sarah-jenkins.png';
import elena from '../../assets/avatars/elena-rodriguez.png';
import admin from '../../assets/avatars/admin-user.png';

type IconType = FunctionComponent<SVGProps<SVGSVGElement>>;
type EngStatus = 'active' | 'upcoming' | 'completed' | 'closed';
type StatKey = 'all' | EngStatus;

interface Engagement {
  id: string;
  name: string;
  division: string;
  status: EngStatus;
  owner: string;
  avatar: string;
  Tile: IconType;
  tileBg: string;
  tileColor: string;
}

const TILES: { Tile: IconType; tileBg: string; tileColor: string }[] = [
  { Tile: TileOutreachIcon, tileBg: 'bg-[#eef2ff]', tileColor: 'text-[#6366f1]' },
  { Tile: TileTownhallIcon, tileBg: 'bg-[#fff7ed]', tileColor: 'text-[#f97316]' },
  { Tile: TileManifestoIcon, tileBg: 'bg-[#f3f4f6]', tileColor: 'text-[#6b7280]' },
  { Tile: TileGrassrootsIcon, tileBg: 'bg-[#eff6ff]', tileColor: 'text-[#3b82f6]' },
];

const AVATARS = [david, sarah, admin, elena];

const RAW: Array<[string, string, EngStatus, string]> = [
  ['Voter Outreach Phase II', 'North Karnataka Division', 'active', 'Rajesh Kumar'],
  ['Town Hall Digital Series', 'National Media Cell', 'active', "Sarah D'Souza"],
  ['Grassroots Mobilization', 'Southern Districts', 'active', 'Lata Hegde'],
  ['Ward Canvassing Drive', 'Central Zone', 'active', 'Rajesh Kumar'],
  ['Youth Connect Program', 'Urban Belt', 'upcoming', 'Meera Nair'],
  ['Farmer Dialogue Series', 'Rural Cell', 'upcoming', 'Amit Shahane'],
  ['Manifesto Feedback Analysis', 'Policy Research Hub', 'completed', 'Amit Shahane'],
  ['Booth Volunteer Onboarding', 'District Ops', 'completed', 'Vikram Singh'],
  ['Door-to-Door Survey', 'Eastern Wards', 'completed', 'Divya Rao'],
  ['Legacy Membership Audit', 'Archive Cell', 'closed', 'Karan Mehta'],
  ['Old Campaign Wind-down', 'National Media Cell', 'closed', 'Sana Khan'],
  ['Regional Leaders Summit', 'HQ', 'upcoming', 'Priyanka Sharma'],
];

const ENGAGEMENTS: Engagement[] = RAW.map(([name, division, status, owner], i) => ({
  id: String(i + 1),
  name,
  division,
  status,
  owner,
  avatar: AVATARS[i % AVATARS.length],
  ...TILES[i % TILES.length],
}));

const STATUS_META: Record<EngStatus, { label: string; pill: string; dot: string }> = {
  active: { label: 'Active', pill: 'bg-[#eff6ff] text-[#2563eb]', dot: 'bg-[#3b82f6]' },
  upcoming: { label: 'Upcoming', pill: 'bg-[#fffbeb] text-[#b45309]', dot: 'bg-[#f59e0b]' },
  completed: { label: 'Completed', pill: 'bg-[#f0fdf4] text-[#166534]', dot: 'bg-[#22c55e]' },
  closed: { label: 'Closed', pill: 'bg-[#f3f4f6] text-[#4b5563]', dot: 'bg-[#9ca3af]' },
};

interface StatCardDef {
  key: StatKey;
  label: string;
  deco: 'trend' | 'progress' | 'icon';
  Icon?: IconType;
  iconColor?: string;
}

const STAT_CARDS: StatCardDef[] = [
  { key: 'all', label: 'Total', deco: 'trend' },
  { key: 'active', label: 'Active', deco: 'progress' },
  { key: 'upcoming', label: 'Upcoming', deco: 'icon', Icon: StatUpcomingIcon, iconColor: 'text-[#cbd5e1]' },
  { key: 'completed', label: 'Completed', deco: 'icon', Icon: StatCompletedIcon, iconColor: 'text-[#60a5fa]' },
  { key: 'closed', label: 'Closed', deco: 'icon', Icon: StatClosedIcon, iconColor: 'text-[#cbd5e1]' },
];

/** Shared 4-column grid template so header and rows stay aligned. */
const GRID = 'grid grid-cols-[minmax(0,1fr)_170px_250px_140px] items-center';

function StatusPill({ status }: { status: EngStatus }) {
  const m = STATUS_META[status];
  return (
    <span className={['inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold', m.pill].join(' ')}>
      <span className={['size-1.5 rounded-full', m.dot].join(' ')} />
      {m.label}
    </span>
  );
}

export default function EngagementsPage() {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState<StatKey>('all');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const counts = useMemo(
    () => ({
      all: ENGAGEMENTS.length,
      active: ENGAGEMENTS.filter((e) => e.status === 'active').length,
      upcoming: ENGAGEMENTS.filter((e) => e.status === 'upcoming').length,
      completed: ENGAGEMENTS.filter((e) => e.status === 'completed').length,
      closed: ENGAGEMENTS.filter((e) => e.status === 'closed').length,
    }),
    [],
  );

  const rows = useMemo(() => {
    const q = query.trim().toLowerCase();
    return ENGAGEMENTS.filter((e) => {
      if (filter !== 'all' && e.status !== filter) return false;
      if (q && !(e.name.toLowerCase().includes(q) || e.owner.toLowerCase().includes(q))) return false;
      return true;
    });
  }, [query, filter]);

  const tableTitle = filter === 'all' ? 'All Engagements' : `${STATUS_META[filter].label} Engagements`;

  return (
    <div className="flex flex-col gap-6">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex flex-col gap-1">
          <span className="text-xs font-bold uppercase tracking-[2.4px] text-brand">
            Operations Overview
          </span>
          <h1 className="text-2xl font-semibold text-ink">Engagements</h1>
        </div>
        <button
          type="button"
          onClick={() => navigate('/user/engagements/new')}
          className="flex items-center justify-center gap-2 self-start rounded-lg bg-brand px-6 py-3 text-base font-medium text-white shadow-[0px_10px_15px_-3px_rgba(30,58,138,0.2),0px_4px_6px_-4px_rgba(30,58,138,0.2)] transition-colors hover:bg-brand-dark sm:self-auto"
        >
          <NewEngagementIcon className="size-5" />
          New Engagement
        </button>
      </div>

      {/* Stats — clickable filters. The selected card takes the blue "Total" treatment. */}
      <div className="grid grid-cols-2 gap-4 sm:gap-6 md:grid-cols-3 xl:grid-cols-5">
        {STAT_CARDS.map((card) => {
          const selected = filter === card.key;
          const value = counts[card.key];
          return (
            <button
              key={card.key}
              type="button"
              onClick={() => setFilter(card.key)}
              aria-pressed={selected}
              className={[
                'flex h-40 flex-col justify-between rounded-3xl p-6 text-left shadow-card transition',
                selected
                  ? 'bg-brand text-white'
                  : 'border border-[#f1f5f9] bg-white hover:border-brand/40',
              ].join(' ')}
            >
              <span className={['text-sm font-medium', selected ? 'text-white/80' : 'text-[#64748b]'].join(' ')}>
                {card.label}
              </span>
              <span
                className={['text-4xl font-bold leading-10', selected ? 'text-white' : 'text-[#1e293b]'].join(' ')}
              >
                {value}
              </span>

              {card.deco === 'trend' && (
                <span
                  className={[
                    'inline-flex w-fit items-center gap-1 rounded px-2 py-1 text-[10px] font-bold',
                    selected ? 'bg-white/10 text-white' : 'bg-brand/10 text-brand',
                  ].join(' ')}
                >
                  <StatTrendIcon className="size-3" />
                  +12% vs LY
                </span>
              )}

              {card.deco === 'progress' && (
                <div
                  className={[
                    'h-1.5 w-full overflow-hidden rounded-full',
                    selected ? 'bg-white/25' : 'bg-[#f1f5f9]',
                  ].join(' ')}
                >
                  <div
                    className={['h-full rounded-full', selected ? 'bg-white' : 'bg-[#2563eb]'].join(' ')}
                    style={{ width: `${counts.all ? Math.round((counts.active / counts.all) * 100) : 0}%` }}
                  />
                </div>
              )}

              {card.deco === 'icon' && card.Icon && (
                <card.Icon className={['size-6', selected ? 'text-white' : card.iconColor].join(' ')} />
              )}
            </button>
          );
        })}
      </div>

      {/* Engagements table */}
      <div className="overflow-hidden rounded-2xl border border-[#f3f4f6] bg-white shadow-card">
        {/* Card header */}
        <div className="flex flex-col gap-4 border-b border-[#f3f4f6] px-6 py-6 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex flex-col gap-0.5">
            <h2 className="text-lg font-bold text-ink">{tableTitle}</h2>
            <p className="text-sm text-ink-secondary">
              {rows.length} {rows.length === 1 ? 'engagement' : 'engagements'}
              {filter !== 'all' ? '' : ' across all statuses'}
            </p>
          </div>
          <div className="relative w-full max-w-[384px]">
            <EngSearchIcon className="pointer-events-none absolute left-3 top-1/2 size-[14px] -translate-y-1/2 text-[#9ca3af]" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search by name or owner..."
              className="w-full rounded-lg bg-[#f9fafb] py-2.5 pl-10 pr-3 text-sm text-ink placeholder:text-[#9ca3af] focus:outline-none focus:ring-2 focus:ring-brand/15"
            />
          </div>
        </div>

        {/* Table */}
        <div className="overflow-x-auto">
          <div className="min-w-[820px]">
            {/* Header row */}
            <div className={[GRID, 'px-6 py-4'].join(' ')}>
              <span className="text-xs font-bold uppercase tracking-[0.6px] text-[#9ca3af]">
                Engagement Details
              </span>
              <span className="text-xs font-bold uppercase tracking-[0.6px] text-[#9ca3af]">Status</span>
              <span className="text-xs font-bold uppercase tracking-[0.6px] text-[#9ca3af]">
                Primary Owner
              </span>
              <span className="text-right text-xs font-bold uppercase tracking-[0.6px] text-[#9ca3af]">
                Actions
              </span>
            </div>

            {/* Rows */}
            {rows.length === 0 ? (
              <div className="border-t border-[#f3f4f6] px-6 py-12 text-center text-sm text-ink-secondary">
                No engagements match this filter.
              </div>
            ) : (
              rows.map((e, i) => (
                <div
                  key={e.id}
                  className={[GRID, 'px-6 py-4', i === 0 ? 'border-t border-[#f3f4f6]' : 'border-t border-[#f9fafb]'].join(' ')}
                >
                  {/* Engagement details */}
                  <div className="flex items-center gap-4 pr-4">
                    <span className={['flex size-10 shrink-0 items-center justify-center rounded-lg', e.tileBg, e.tileColor].join(' ')}>
                      <e.Tile className="size-5" />
                    </span>
                    <div className="flex min-w-0 flex-col">
                      <button
                        type="button"
                        onClick={() => navigate(`/user/engagements/${e.id}`, { state: { name: e.name } })}
                        className="truncate text-left text-sm font-semibold text-brand hover:underline"
                      >
                        {e.name}
                      </button>
                      <span className="truncate text-xs text-[#6b7280]">{e.division}</span>
                    </div>
                  </div>

                  {/* Status */}
                  <div>
                    <StatusPill status={e.status} />
                  </div>

                  {/* Primary owner */}
                  <div className="flex items-center gap-3">
                    <img src={e.avatar} alt={e.owner} className="size-8 shrink-0 rounded-full object-cover" />
                    <span className="text-sm font-medium text-[#111827]">{e.owner}</span>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center justify-end gap-3 text-ink-secondary">
                    <button type="button" aria-label="Edit" className="transition-colors hover:text-brand">
                      <EditSquareIcon className="size-4" />
                    </button>
                    <button
                      type="button"
                      aria-label="Delete"
                      className="transition-colors hover:text-red-500"
                    >
                      <EngDeleteIcon className="h-[14px] w-[12.25px]" />
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Footer */}
        {(() => {
          const total = filter === 'all' ? counts.all : counts[filter];
          return (
            <Pagination
              rangeStart={total === 0 ? 0 : (page - 1) * pageSize + 1}
              rangeEnd={Math.min(page * pageSize, total)}
              total={total}
              noun="engagements"
              page={page}
              totalPages={Math.max(1, Math.ceil(total / pageSize))}
              onChange={setPage}
              pageSize={pageSize}
              onPageSizeChange={(v) => {
                setPageSize(v);
                setPage(1);
              }}
            />
          );
        })()}
      </div>
    </div>
  );
}
